import { useEffect, useRef, useState } from 'react';
import {
  ChevronRight,
  Info,
  Link as LinkIcon,
  Loader2,
  Type,
  Upload,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button, Sheet, cn } from '@ai-tutor/ui';
import { AddLinkTab } from './tabs/AddLinkTab';
import { PasteTextTab } from './tabs/PasteTextTab';
import type { TabFocusHandle } from './tabs/UploadFileTab';
import { SupportedFormatsModal } from './SupportedFormatsModal';
import { useStageFiles } from './useStageFiles';
import { useParseLimits } from './useParseLimits';
import {
  ACCEPT_ATTR,
  MAX_FILE_SIZE_MB,
  type AttachTabId,
  type StagedSource,
} from './types';

export interface SessionEntry {
  kind: AttachTabId;
  label: string;
}

type Step = 'choose' | 'link' | 'text';

const STEP_TITLE: Record<Step, string> = {
  choose: 'Attach source',
  link: 'Add a link',
  text: 'Paste text',
};

export const SESSION_ICON: Record<AttachTabId, LucideIcon> = {
  file: Upload,
  link: LinkIcon,
  text: Type,
};

export interface AttachSourceSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (source: StagedSource) => Promise<void>;
  remainingFileSlots?: number;
  initialTab?: AttachTabId;
  sessionAdds: SessionEntry[];
  onRemoveEntry: (index: number) => void;
}

/**
 * Attaching a source on a phone: a bottom sheet that starts with the three
 * ways in, the pattern phones use for "add something" (a share or attach
 * sheet), not the desktop's tabbed dialog.
 *
 * - **Upload a file** opens the phone's own picker at once. The desktop's
 *   drop zone - "Drop files here" - means nothing without a mouse.
 * - **Add a link** and **Paste text** are a step inside the sheet, with a
 *   back arrow. Each stays open after an add, cleared for the next one, as
 *   the desktop tabs do, so several links go in without retracing - with
 *   the session's adds listed above the form, so each add shows it landed.
 * - What was added this session lists at the top of the first step, each
 *   removable, and Done is pinned under it in thumb reach.
 *
 * Validation and the slot cap are `useStageFiles`, shared with the desktop
 * drop zone.
 */
export function AttachSourceSheet({
  isOpen,
  onClose,
  onAdd,
  remainingFileSlots,
  initialTab,
  sessionAdds,
  onRemoveEntry,
}: AttachSourceSheetProps) {
  const [step, setStep] = useState<Step>('choose');
  const linkRef = useRef<TabFocusHandle>(null);
  const textRef = useRef<TabFocusHandle>(null);

  useEffect(() => {
    if (isOpen) {
      setStep(
        initialTab === 'link' || initialTab === 'text' ? initialTab : 'choose'
      );
    }
  }, [isOpen, initialTab]);

  // Into a form step: its field takes focus, so the keyboard comes up.
  useEffect(() => {
    if (!isOpen || step === 'choose') return;
    const id = requestAnimationFrame(() =>
      (step === 'link' ? linkRef : textRef).current?.focus()
    );
    return () => cancelAnimationFrame(id);
  }, [isOpen, step]);

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      side='bottom'
      size='auto'
      title={STEP_TITLE[step]}
      onBack={step === 'choose' ? undefined : () => setStep('choose')}
    >
      {step === 'choose' ? (
        <ChooseStep
          onAdd={onAdd}
          remainingFileSlots={remainingFileSlots}
          onPick={setStep}
          onDone={onClose}
          sessionAdds={sessionAdds}
          onRemoveEntry={onRemoveEntry}
        />
      ) : (
        <div className='min-h-0 flex-1 overflow-y-auto pb-4'>
          {/* What went in this session, here too: these steps stay open
              after an add, and without it nothing on screen said the link
              or text had been taken. */}
          {sessionAdds.length > 0 && (
            <div className='px-4 pt-3'>
              <SessionList entries={sessionAdds} onRemove={onRemoveEntry} />
            </div>
          )}
          {step === 'link' ? (
            <AddLinkTab ref={linkRef} onAdd={onAdd} />
          ) : (
            <PasteTextTab ref={textRef} onAdd={onAdd} />
          )}
        </div>
      )}
    </Sheet>
  );
}

interface ChooseStepProps
  extends Pick<
    AttachSourceSheetProps,
    'onAdd' | 'remainingFileSlots' | 'sessionAdds' | 'onRemoveEntry'
  > {
  onPick: (step: Step) => void;
  onDone: () => void;
}

function ChooseStep({
  onAdd,
  remainingFileSlots,
  onPick,
  onDone,
  sessionAdds,
  onRemoveEntry,
}: ChooseStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showFormats, setShowFormats] = useState(false);
  const { stageFiles, isBusy } = useStageFiles(onAdd, remainingFileSlots);
  const noSlots = remainingFileSlots === 0;
  const limitsNote = useParseLimits().note;
  const slotsNote =
    (remainingFileSlots !== undefined ? ` · ${remainingFileSlots} left` : '') +
    limitsNote;

  return (
    <>
      <div className='min-h-0 flex-1 overflow-y-auto px-4 py-3'>
        <SessionList entries={sessionAdds} onRemove={onRemoveEntry} />

        <Choices
          busy={isBusy}
          noSlots={noSlots}
          slotsNote={slotsNote}
          onUpload={() => inputRef.current?.click()}
          onPick={onPick}
        />

        <button
          type='button'
          onClick={() => setShowFormats(true)}
          className='mt-2 flex min-h-11 items-center gap-1.5 rounded-md px-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground'
        >
          <Info className='h-3.5 w-3.5' />
          Supported file types
        </button>

        {/* Driven by the Upload row; kept out of layout so the OS draws no
            "No file chosen" of its own. */}
        <input
          ref={inputRef}
          type='file'
          multiple
          accept={ACCEPT_ATTR}
          disabled={noSlots}
          onChange={e => {
            const picked = Array.from(e.target.files ?? []);
            e.target.value = '';
            if (picked.length > 0) void stageFiles(picked);
          }}
          className='hidden'
          tabIndex={-1}
          aria-hidden='true'
        />
      </div>

      <div className='flex-shrink-0 border-t border-border px-4 py-3'>
        <Button type='button' onClick={onDone} className='h-11 w-full'>
          Done
        </Button>
      </div>

      <SupportedFormatsModal
        isOpen={showFormats}
        onClose={() => setShowFormats(false)}
      />
    </>
  );
}

/** The three ways in. Upload opens the picker; the others are steps. */
function Choices({
  busy,
  noSlots,
  slotsNote,
  onUpload,
  onPick,
}: {
  busy: boolean;
  noSlots: boolean;
  slotsNote: string;
  onUpload: () => void;
  onPick: (step: Step) => void;
}) {
  return (
    <div className='divide-y divide-border overflow-hidden rounded-xl border border-border'>
      <ChoiceRow
        icon={busy ? Loader2 : Upload}
        spin={busy}
        title={busy ? 'Uploading…' : 'Upload a file'}
        detail={`PDF, Word, slides, images · ${MAX_FILE_SIZE_MB} MB${slotsNote}`}
        disabled={noSlots || busy}
        onClick={onUpload}
      />
      <ChoiceRow
        icon={LinkIcon}
        title='Add a link'
        detail='A web page or a YouTube video'
        onClick={() => onPick('link')}
        chevron
      />
      <ChoiceRow
        icon={Type}
        title='Paste text'
        detail='Your notes, or an excerpt'
        onClick={() => onPick('text')}
        chevron
      />
    </div>
  );
}

function ChoiceRow({
  icon: Icon,
  title,
  detail,
  onClick,
  disabled,
  chevron,
  spin,
}: {
  icon: LucideIcon;
  title: string;
  detail: string;
  onClick: () => void;
  disabled?: boolean;
  chevron?: boolean;
  spin?: boolean;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className='flex w-full items-center gap-3 bg-card px-3 py-3 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50'
    >
      <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-foreground'>
        <Icon
          className={cn(
            'h-5 w-5',
            spin && 'animate-spin motion-reduce:animate-none'
          )}
          aria-hidden='true'
        />
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block text-sm font-medium text-foreground'>
          {title}
        </span>
        <span className='block truncate text-xs text-muted-foreground'>
          {detail}
        </span>
      </span>
      {chevron && (
        <ChevronRight
          className='h-4 w-4 flex-shrink-0 text-muted-foreground'
          aria-hidden='true'
        />
      )}
    </button>
  );
}

/** What this session added, newest last, each removable. */
function SessionList({
  entries,
  onRemove,
}: {
  entries: SessionEntry[];
  onRemove: (index: number) => void;
}) {
  if (entries.length === 0) return null;
  return (
    <section aria-label='Added in this session' className='mb-3'>
      <p className='mb-1.5 text-xs font-medium text-muted-foreground'>
        Added ({entries.length})
      </p>
      <ul className='space-y-1'>
        {entries.map((entry, i) => {
          const Icon = SESSION_ICON[entry.kind];
          return (
            <li
              key={`${entry.kind}-${i}`}
              className='flex items-center gap-2 rounded-lg bg-muted/60 pl-3'
            >
              <Icon
                className='h-4 w-4 flex-shrink-0 text-muted-foreground'
                aria-hidden='true'
              />
              <span className='min-w-0 flex-1 truncate text-sm text-foreground'>
                {entry.label}
              </span>
              <button
                type='button'
                aria-label={`Remove ${entry.label}`}
                onClick={() => onRemove(i)}
                className='flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-destructive-emphasis'
              >
                <X className='h-4 w-4' />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
